/**
 * TanStack Query hook for `GET /api/cro/admin/targets`.
 *
 * Returns the full targets list (all versions) so the AdminTargetsPage can:
 *   1. Show the current target value (max-version row per metric)
 *   2. Build per-row history sparklines from prior versions
 *
 * Config:
 *   - queryKey: ['cro', 'targets']
 *   - staleTime: 30s — target edits are infrequent; short enough to show
 *     a save result within ~30s if the user refreshes the page
 *   - retry: 2 — standard retry for blips; admin route is stable
 *
 * Per PATTERNS.md "useCroTargets.ts (NEW)" — exact analog shape from
 * useCroScorecard.ts adapted for the CroTargetsListResponse shape.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  apiFetch,
  type ApiClientError,
  type ApiServerError,
  type UnauthenticatedError,
} from '../lib/api-client.js';

import type { CroTargetsListResponse } from '@saledash/shared';

export type TargetsError = ApiServerError | ApiClientError | UnauthenticatedError;

export function useCroTargets(): UseQueryResult<CroTargetsListResponse, TargetsError> {
  return useQuery<CroTargetsListResponse, TargetsError>({
    queryKey: ['cro', 'targets'],
    queryFn: ({ signal }) =>
      apiFetch<CroTargetsListResponse>('/api/cro/admin/targets', { signal }),
    staleTime: 30_000,
    retry: 2,
  });
}
